import React, { useCallback } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { noop } from 'ui-components/default-props/noop';

import { AutoCompleteField } from '../../constants/autocomplete-field';
import { useSortedTranslatedCountries } from '../../hooks/use-sorted-translated-countries';
import { useUniqueHtmlId } from '../../hooks/use-unique-html-id';

import style from '../dropdown-style.module.less';

const elementId = 'address-countryCode';

export const CountryDropdown = ({
  componentClassName,
  hasError = false,
  onChange,
  value = '',
  disabled = false,
  label,
  labelClassName,
  dropdownClassName,
  onClick = noop,
  allowEmptyValueToSelect = false,
}) => {
  const countries = useSortedTranslatedCountries({ labelProp: 'name' });

  const id = useUniqueHtmlId(elementId);

  const selectedCountry = countries.find((c) => c.code === value);
  const dropdownTitle = selectedCountry?.name ?? '';

  const onInputChange = useCallback(
    (event) => {
      const targetValue = event?.target?.value || '';
      const targetCountry =
        targetValue.length === 2
          ? countries.find((state) => state.code === targetValue)
          : countries.find((state) => state.name === targetValue);

      if (targetCountry) {
        onChange(targetCountry.code);
      }
    },
    [countries, onChange],
  );

  return (
    <div className={componentClassName}>
      {label && (
        <label className={labelClassName} htmlFor={id}>
          {label}
        </label>
      )}
      <DropdownMenu
        className={classnames(dropdownClassName, style['country-dropdown'])}
        disabled={disabled}
        dropdownMenuShouldHaveWidthOfButton
        hasError={hasError}
        id={id}
        variant='form'
      >
        <DropdownMenuToggler className={style['country-toggler']} onClick={onClick} tabIndex={-1} theme='white'>
          <input
            autoComplete={AutoCompleteField.Country}
            className={style['country-input']}
            onChange={onInputChange}
            value={dropdownTitle}
          />
        </DropdownMenuToggler>
        {allowEmptyValueToSelect && <DropdownMenuItem onClick={() => onChange('')}>&nbsp;</DropdownMenuItem>}
        {countries.map(({ code, name }) => (
          <DropdownMenuItem key={code} onClick={() => onChange(code)} selected={code === value}>
            {name}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
      {/*
       The data-recurly prop is necessary here to identify non card billing fields for Recurly.js docs here  https://developers.recurly.com/reference/recurly-js/#build-a-payment-form-with-elements
      */}
      <input data-private='' data-recurly='country' type='hidden' value={selectedCountry?.code || ''} />
    </div>
  );
};

CountryDropdown.propTypes = {
  componentClassName: PropTypes.string,
  hasError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.node,
  labelClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  onClick: PropTypes.func,
  allowEmptyValueToSelect: PropTypes.bool,
};
